import { useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxForminput } from '@/components/01-atoms/Forminput/Forminput'
import { PhnxFormselect } from '@/components/01-atoms/Formselect/Formselect'
import { PhnxJotformiframe } from '@/components/01-atoms/Jotformiframe/Jotformiframe'
import { PhnxForm } from '@/components/03-organisms/Form/Form'
import { GoogleMap } from '@/components/maps/GoogleMap'

import HoursIcon from '../../../../public/svg/hours.svg'
import OfficeIcon from '../../../../public/svg/location.svg'
import PhoneIcon from '../../../../public/svg/phone.svg'
import styles from './OfficeDetails.module.css'

interface OfficeDetailsProps {
  address: string
  phone: string
  hours: string
  formId: string
  form: any
  mailingAddress: string
  location: { lat: number; long: number }
}

interface OfficeDetailsMainProps extends OfficeDetailsProps {
  onContactButtonClick: () => void
}

interface OfficeDetailsFormProps extends OfficeDetailsProps {
  onBackButtonClick: () => void
}

function transformFormFields(formData: any) {
  if (!formData?.fields) return []

  return formData.fields.map((row: any) => {
    if (row.component === 'formRow') {
      return row.fields
        .map((field: any) => {
          if (field.component === 'formInput') {
            return (
              <PhnxForminput
                key={field._uid}
                variant={field.variant}
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                isRequired={field.required}
                showRequired={field.showRequired}
                radioOptions={field.radioOptions}
                customAnswer={field.customField_answer}
                hiddenValue={field.hiddenValue}
              />
            )
          } else if (field.component === 'formSelect') {
            return (
              <PhnxFormselect
                key={field._uid}
                variant={field.variant}
                name={field.name}
                label={field.label}
                options={field.options}
                multiOptions={field.multiOptions}
                isRequired={field.required}
                showRequired={field.showRequired}
                phoneEnquiryOptions={[
                  { Personal: field.phoneEnquiryPersonal || [] },
                  { Business: field.phoneEnquiryBusiness || [] },
                ]}
              />
            )
          }
          return null
        })
        .filter(Boolean)
    } else if (row.component === 'formInput') {
      // Handle standalone form inputs (like hidden fields)
      return [
        <PhnxForminput
          key={row._uid}
          variant={row.variant}
          name={row.name}
          label={row.label}
          placeholder={row.placeholder}
          isRequired={row.required}
          showRequired={row.showRequired}
          hiddenValue={row.hiddenValue}
        />,
      ]
    }
    return []
  })
}

const OfficeDetailsForm = ({ formId, onBackButtonClick, form }: OfficeDetailsFormProps) => {
  const keyDownHandler: React.HTMLAttributes<HTMLElement>['onKeyDown'] = (event) => {
    if (event.key === 'Enter') {
      onBackButtonClick()
    }
  }

  const formToRender = form[0]

  const transformedFields = transformFormFields(formToRender)

  return (
    <div data-testid="details-form" className={styles['details-form']}>
      <button
        className={styles['form-heading']}
        data-testid="back-to-details"
        onClick={onBackButtonClick}
        onKeyDown={keyDownHandler}
        tabIndex={0}
        aria-label={`Back to details`}
      >
        <i className={`fx-icon fx-icon-arrow-down-lg ${styles['back-icon']}`}></i>
        Back to details
      </button>
      {form &&
        (Array.isArray(form) && form.length > 0 && form[0]?.component === 'Jotform' ? (
          <PhnxJotformiframe title="Back to details" formId={formId} />
        ) : (
          <PhnxForm
            formID="details-form"
            fields={transformedFields}
            endpoint={formToRender.endpoint}
            title={formToRender.title}
            ctaLabel={formToRender.cta_label}
            ctaVariant="primary"
            ctaFullMobile={formToRender.cta_full_mobile}
            ctaPosition={formToRender.cta_position || 'center'}
            ctaBold={formToRender.cta_bold}
            backgroundColor={formToRender.form_color}
            textColor={formToRender.textColour}
            disclaimer={formToRender.disclaimer}
            subContent={formToRender.subContent}
            lead_source={formToRender.lead_source}
            campaign_id={formToRender.campaign_id}
            lead_type={formToRender.lead_type}
            submission_url={formToRender.submission_url}
            isSubscribeForm={formToRender.isSubscribeForm}
            variant={formToRender.variant}
            shadow={formToRender.shadow}
          />
        ))}
      {/* {!formId && <div>No form ID</div>} */}
    </div>
  )
}

const OfficeDetailsMain = ({
  onContactButtonClick,
  location,
  address,
  phone,
  hours,
  mailingAddress,
}: OfficeDetailsMainProps) => {
  return (
    <>
      <div className={styles['office-info']}>
        <div>
          {address && (
            <>
              <span className={styles['office-info__label']}>
                <OfficeIcon viewBox="0 0 26 36" className="inline mr-1 lg:mr-2.5" /> Office address:
              </span>
              <p>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.long}`}
                >
                  {address}
                </a>
              </p>
            </>
          )}

          {phone && (
            <>
              <span className={styles['office-info__label']}>
                <PhoneIcon viewBox="0 0 25 25" className="inline mr-2 lg:mr-5" />
                Phone:
              </span>
              <p>
                <a href={`tel:${phone}`}>{phone}</a>
              </p>
            </>
          )}

          {hours && (
            <>
              <span className={styles['office-info__label']}>
                <HoursIcon viewBox="0 0 28 30" className="inline mr-1 lg:mr-2" /> Hours:
              </span>
              <p dangerouslySetInnerHTML={{ __html: hours }} />
            </>
          )}

          {mailingAddress && (
            <>
              <span className={styles['office-info__label']}>
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4 inline-block mr-1 -mt-[2px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>{' '}
                Mailing address:
              </span>
              <p>{mailingAddress}</p>
            </>
          )}
        </div>

        <div className={styles['button-group']}>
          <PhnxButton
            size="large"
            type="link"
            label="Contact Findex"
            onClick={onContactButtonClick}
            data-testid="contact-btn"
          >
            Contact Findex
          </PhnxButton>

          <PhnxButton
            css={{ fontWeight: 600 }}
            variant="quaternary"
            size="large"
            type="link"
            href={`https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.long}`}
            label="Office directions"
            target="_blank" // Open in new tab
            data-testid="office-direction-btn"
          />
        </div>
      </div>

      <div className={(styles['map-wrapper'], 'map-wrapper')}>
        <GoogleMap lat={Number(location.lat)} lng={Number(location.long)} />
      </div>
    </>
  )
}

export const PhnxOfficeDetails: React.FC<OfficeDetailsProps> = (props) => {
  const [showForm, setShowForm] = useState(false)
  return (
    <div className={styles['office-details']}>
      {!showForm && <OfficeDetailsMain onContactButtonClick={() => setShowForm(true)} {...props} />}
      {showForm && <OfficeDetailsForm onBackButtonClick={() => setShowForm(false)} {...props} />}
    </div>
  )
}
